import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COLLECTION = 'userModerationStatus';

const normalize = (snap) => {
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    commentBan: !!data.commentBan,
    siteBan: !!data.siteBan,
    muteUntil: data.muteUntil || null,
    reason: data.reason || '',
    setBy: data.setBy || null,
    setAt: data.setAt || null,
  };
};

export const listenToUserModerationStatus = (uid, callback, onError) => {
  if (!uid) {
    callback(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, COLLECTION, uid),
    (snap) => callback(normalize(snap)),
    (err) => {
      if (onError) onError(err);
      else console.error('listenToUserModerationStatus:', err);
      callback(null);
    }
  );
};

export const getUserModerationStatus = async (uid) => {
  if (!uid) return null;
  const snap = await getDoc(doc(db, COLLECTION, uid));
  return normalize(snap);
};

export const setModerationStatus = async (
  targetUid,
  { commentBan = false, siteBan = false, muteUntil = null, reason = '' },
  modUid
) => {
  if (!targetUid) throw new Error('targetUid is required');
  let muteTimestamp = null;
  if (muteUntil) {
    if (muteUntil instanceof Date) muteTimestamp = Timestamp.fromDate(muteUntil);
    else if (muteUntil instanceof Timestamp) muteTimestamp = muteUntil;
    else if (typeof muteUntil === 'number') muteTimestamp = Timestamp.fromMillis(muteUntil);
  }
  await setDoc(doc(db, COLLECTION, targetUid), {
    commentBan: !!commentBan,
    siteBan: !!siteBan,
    muteUntil: muteTimestamp,
    reason: reason || '',
    setBy: modUid || null,
    setAt: serverTimestamp(),
  });
};

export const clearModerationStatus = (targetUid) => {
  if (!targetUid) throw new Error('targetUid is required');
  return deleteDoc(doc(db, COLLECTION, targetUid));
};

// Returns ms remaining if mute is active, otherwise 0.
export const muteRemainingMs = (status) => {
  if (!status?.muteUntil) return 0;
  const ms =
    status.muteUntil.toMillis?.() ??
    (status.muteUntil.seconds ? status.muteUntil.seconds * 1000 : 0);
  return Math.max(0, ms - Date.now());
};

export const isMuteActive = (status) => muteRemainingMs(status) > 0;

// Lists everyone with an active commentBan/siteBan/mute. Mods only.
export const listenToActiveModerationStatuses = (callback, onError) => {
  // Firestore can't combine the three "any of" fields cheaply; we listen to the
  // whole collection (small) and filter client-side.
  return onSnapshot(
    query(collection(db, COLLECTION), where('setAt', '!=', null)),
    (snap) => callback(snap.docs.map((d) => normalize(d))),
    (err) => {
      if (onError) onError(err);
      else console.error('listenToActiveModerationStatuses:', err);
    }
  );
};
